import { useState, useRef, useCallback, useEffect } from 'react';
import { ZoomIn, ZoomOut, RotateCw, Move, Check, X, Wand2, Sliders, ChevronLeft, ChevronRight, Smartphone, Monitor } from 'lucide-react';
// @ts-ignore - Open source libraries
import smartcrop from 'smartcrop';
import './ImageCropTool.css';

interface ImageCropToolProps {
  imageFile: File;
  aspectRatio?: number; // width/height ratio (e.g., 1 for square, 16/9 for landscape)
  onCropComplete: (croppedFile: File, croppedDataUrl: string) => void;
  onCancel: () => void;
  cropType?: 'square' | 'landscape' | 'portrait' | 'custom';
  enableFilters?: boolean;
  enableSmartCrop?: boolean;
}

interface CropArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface FilterSettings {
  brightness: number;
  contrast: number;
  saturation: number;
  sepia: number;
  grayscale: number;
  invert: number;
  blur: number;
  hueRotate: number;
}

const ASPECT_RATIOS = {
  square: 1,
  landscape: 16 / 9,
  portrait: 9 / 16,
  custom: 0
};

// Instagram-like filter presets
const FILTER_PRESETS = {
  normal: { brightness: 0, contrast: 0, saturation: 0, sepia: 0, grayscale: 0, invert: 0, blur: 0, hueRotate: 0 },
  vintage: { brightness: 0.1, contrast: 0.2, saturation: -0.2, sepia: 0.3, grayscale: 0, invert: 0, blur: 0, hueRotate: 0 },
  dramatic: { brightness: -0.1, contrast: 0.4, saturation: 0.2, sepia: 0, grayscale: 0, invert: 0, blur: 0, hueRotate: 0 },
  warm: { brightness: 0.1, contrast: 0.1, saturation: 0.2, sepia: 0.1, grayscale: 0, invert: 0, blur: 0, hueRotate: 30 },
  cool: { brightness: 0, contrast: 0.1, saturation: 0.1, sepia: 0, grayscale: 0, invert: 0, blur: 0, hueRotate: -30 },
  blackwhite: { brightness: 0, contrast: 0.2, saturation: 0, sepia: 0, grayscale: 1, invert: 0, blur: 0, hueRotate: 0 },
  dreamy: { brightness: 0.2, contrast: -0.1, saturation: 0.1, sepia: 0.1, grayscale: 0, invert: 0, blur: 1, hueRotate: 0 },
  vibrant: { brightness: 0.1, contrast: 0.3, saturation: 0.4, sepia: 0, grayscale: 0, invert: 0, blur: 0, hueRotate: 0 }
};

const ImageCropTool = ({
  imageFile,
  aspectRatio,
  onCropComplete,
  onCancel,
  cropType = 'square',
  enableFilters = true,
  enableSmartCrop = true
}: ImageCropToolProps) => {
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [scale, setScale] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [cropArea, setCropArea] = useState<CropArea>({ x: 0, y: 0, width: 300, height: 300 });
  const [isCropDragging, setIsCropDragging] = useState(false);
  const [cropDragStart, setCropDragStart] = useState({ x: 0, y: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [showFilterAdjustments, setShowFilterAdjustments] = useState(false);
  const [filters, setFilters] = useState<FilterSettings>(FILTER_PRESETS.normal);
  const [selectedPreset, setSelectedPreset] = useState('normal');
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentCropType, setCurrentCropType] = useState(cropType);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const filtersScrollRef = useRef<HTMLDivElement>(null);

  const finalAspectRatio = aspectRatio || ASPECT_RATIOS[currentCropType];

  // Load and display image
  useEffect(() => {
    const img = new Image();
    img.onload = async () => {
      setImage(img);
      setIsLoading(false);
      
      // Calculate initial crop area
      const containerWidth = 400;
      const containerHeight = 400;
      
      let cropWidth, cropHeight;
      
      if (finalAspectRatio > 0) {
        if (finalAspectRatio >= 1) {
          // Landscape or square
          cropWidth = Math.min(containerWidth * 0.8, 300);
          cropHeight = cropWidth / finalAspectRatio;
        } else {
          // Portrait
          cropHeight = Math.min(containerHeight * 0.8, 300);
          cropWidth = cropHeight * finalAspectRatio;
        }
      } else {
        // Custom ratio
        cropWidth = 300;
        cropHeight = 300;
      }

      let initialCropArea = {
        x: (containerWidth - cropWidth) / 2,
        y: (containerHeight - cropHeight) / 2,
        width: cropWidth,
        height: cropHeight
      };

      // Use smart crop if enabled
      if (enableSmartCrop) {
        try {
          const result = await smartcrop.crop(img, { 
            width: Math.round(cropWidth), 
            height: Math.round(cropHeight) 
          });
          
          if (result.topCrop) {
            // Scale the smart crop coordinates to our container
            const scaleX = containerWidth / img.width;
            const scaleY = containerHeight / img.height;
            const displayScale = Math.min(scaleX, scaleY);
            
            initialCropArea = {
              x: result.topCrop.x * displayScale,
              y: result.topCrop.y * displayScale,
              width: cropWidth,
              height: cropHeight
            };
          }
        } catch (error) {
          console.warn('Smart crop failed, using center crop:', error);
        }
      }
      
      setCropArea(initialCropArea);
      updatePreview();
    };
    
    img.src = URL.createObjectURL(imageFile);
    
    return () => {
      URL.revokeObjectURL(img.src);
    };
  }, [imageFile, finalAspectRatio, enableSmartCrop]);

  // Update preview with filters
  const updatePreview = useCallback(() => {
    if (!image || !previewCanvasRef.current) return;

    const canvas = previewCanvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas dimensions based on rotation
    if (rotation === 90 || rotation === 270) {
      canvas.width = image.height;
      canvas.height = image.width;
    } else {
      canvas.width = image.width;
      canvas.height = image.height;
    }

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Save context for transformations
    ctx.save();

    // Apply rotation with proper translation
    if (rotation !== 0) {
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate((rotation * Math.PI) / 180);
      
      // Translate back based on original image dimensions
      if (rotation === 90 || rotation === 270) {
        ctx.translate(-image.width / 2, -image.height / 2);
      } else {
        ctx.translate(-image.width / 2, -image.height / 2);
      }
    }

    // Apply filters using CSS filter style
    const filterString = `
      brightness(${1 + filters.brightness})
      contrast(${1 + filters.contrast})
      saturate(${1 + filters.saturation})
      sepia(${filters.sepia})
      grayscale(${filters.grayscale})
      invert(${filters.invert})
      blur(${filters.blur}px)
      hue-rotate(${filters.hueRotate}deg)
    `;
    
    ctx.filter = filterString;
    ctx.drawImage(image, 0, 0);
    ctx.filter = 'none';

    // Restore context
    ctx.restore();
  }, [image, filters, rotation]);

  useEffect(() => {
    updatePreview();
  }, [updatePreview]);

  // Handle mouse/touch events for dragging
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  }, [position]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging) return;
    
    setPosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    });
  }, [isDragging, dragStart]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Handle mouse/touch events for crop area dragging
  const handleCropMouseDown = useCallback((e: React.MouseEvent) => {
    if (!containerRef.current) return;
    
    e.stopPropagation(); // Prevent image dragging when dragging crop area
    setIsCropDragging(true);
    
    const containerRect = containerRef.current.getBoundingClientRect();
    setCropDragStart({
      x: e.clientX - containerRect.left - cropArea.x,
      y: e.clientY - containerRect.top - cropArea.y
    });
  }, [cropArea]);

  const handleCropMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isCropDragging || !containerRef.current) return;
    
    const containerRect = containerRef.current.getBoundingClientRect();
    const newX = e.clientX - containerRect.left - cropDragStart.x;
    const newY = e.clientY - containerRect.top - cropDragStart.y;
    
    // Constrain crop area within container bounds
    const maxX = containerRect.width - cropArea.width;
    const maxY = containerRect.height - cropArea.height;
    
    setCropArea({
      x: Math.max(0, Math.min(newX, maxX)),
      y: Math.max(0, Math.min(newY, maxY)),
      width: cropArea.width,
      height: cropArea.height
    });
  }, [isCropDragging, cropDragStart, cropArea]);

  const handleCropMouseUp = useCallback(() => {
    setIsCropDragging(false);
  }, []);

  // Rotation
  const handleRotate = useCallback(() => {
    console.log('Rotating image, current rotation:', rotation);
    setRotation(prev => {
      const newRotation = (prev + 90) % 360;
      console.log('New rotation:', newRotation);
      return newRotation;
    });
    // Force preview update
    setTimeout(() => updatePreview(), 100);
  }, [rotation, updatePreview]);

  // Reset position and scale
  const handleReset = useCallback(() => {
    console.log('Resetting crop tool');
    setScale(1);
    setRotation(0);
    setPosition({ x: 0, y: 0 });
    setFilters(FILTER_PRESETS.normal);
    setSelectedPreset('normal');
    // Force preview update
    setTimeout(() => updatePreview(), 100);
  }, [updatePreview]);

  // Toggle between portrait and landscape
  const handleToggleCropType = useCallback(() => {
    const newCropType = currentCropType === 'portrait' ? 'landscape' : 'portrait';
    setCurrentCropType(newCropType);
    
    // Recalculate crop area for new aspect ratio
    const newAspectRatio = ASPECT_RATIOS[newCropType];
    const containerWidth = 400;
    const containerHeight = 400;
    
    let cropWidth, cropHeight;
    if (newAspectRatio >= 1) {
      // Landscape or square
      cropWidth = Math.min(containerWidth * 0.8, 300);
      cropHeight = cropWidth / newAspectRatio;
    } else {
      // Portrait
      cropHeight = Math.min(containerHeight * 0.8, 300);
      cropWidth = cropHeight * newAspectRatio;
    }
    
    setCropArea({
      x: (containerWidth - cropWidth) / 2,
      y: (containerHeight - cropHeight) / 2,
      width: cropWidth,
      height: cropHeight
    });
  }, [currentCropType]);

  // Zoom controls
  const handleZoomIn = useCallback(() => {
    setScale(prev => Math.min(prev + 0.1, 3));
  }, []);

  const handleZoomOut = useCallback(() => {
    setScale(prev => Math.max(prev - 0.1, 0.1));
  }, []);

  // Smart crop suggestion
  const handleSmartCrop = useCallback(async () => {
    if (!image || !enableSmartCrop) {
      console.warn('Smart crop not available - image:', !!image, 'enabled:', enableSmartCrop);
      return;
    }
    
    console.log('Starting smart crop...');
    setIsProcessing(true);
    
    try {
      console.log('Image dimensions:', image.width, 'x', image.height);
      console.log('Crop area:', cropArea);
      
      const result = await smartcrop.crop(image, { 
        width: Math.round(cropArea.width), 
        height: Math.round(cropArea.height) 
      });
      
      console.log('Smart crop result:', result);
      
      if (result.topCrop) {
        const containerRect = containerRef.current?.getBoundingClientRect();
        if (containerRect) {
          const scaleX = containerRect.width / image.width;
          const scaleY = containerRect.height / image.height;
          const displayScale = Math.min(scaleX, scaleY);
          
          const newCropArea = {
            ...cropArea,
            x: result.topCrop.x * displayScale,
            y: result.topCrop.y * displayScale
          };
          
          console.log('New crop area:', newCropArea);
          setCropArea(newCropArea);
        }
      } else {
        console.warn('No top crop found in result');
      }
    } catch (error) {
      console.error('Smart crop failed:', error);
      // Show user-friendly error
      alert('Smart crop failed. Please try manually adjusting the crop area.');
    } finally {
      setIsProcessing(false);
    }
  }, [image, cropArea, enableSmartCrop]);

  // Filter functions
  const applyFilterPreset = useCallback((presetName: string) => {
    const preset = FILTER_PRESETS[presetName as keyof typeof FILTER_PRESETS];
    if (preset) {
      setFilters(preset);
      setSelectedPreset(presetName);
    }
  }, []);

  // Filter scroll controls
  const scrollFiltersLeft = useCallback(() => {
    if (filtersScrollRef.current) {
      filtersScrollRef.current.scrollBy({ left: -200, behavior: 'smooth' });
    }
  }, []);

  const scrollFiltersRight = useCallback(() => {
    if (filtersScrollRef.current) {
      filtersScrollRef.current.scrollBy({ left: 200, behavior: 'smooth' });
    }
  }, []);

  // Generate cropped image
  const handleCropComplete = useCallback(async () => {
    if (!image || !canvasRef.current) return;

    setIsProcessing(true);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size to crop area
    canvas.width = cropArea.width;
    canvas.height = cropArea.height;

    // Calculate the source coordinates on the original image
    const containerRect = containerRef.current?.getBoundingClientRect();
    if (!containerRect) return;

    // Calculate scale factors
    const displayWidth = image.width * scale;
    const displayHeight = image.height * scale;
    
    // Calculate source rectangle
    const sourceX = (cropArea.x - position.x - containerRect.width / 2 + displayWidth / 2) / scale;
    const sourceY = (cropArea.y - position.y - containerRect.height / 2 + displayHeight / 2) / scale;
    const sourceWidth = cropArea.width / scale;
    const sourceHeight = cropArea.height / scale;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Save context for rotation and filters
    ctx.save();

    // Apply rotation if needed
    if (rotation !== 0) {
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate((rotation * Math.PI) / 180);
      ctx.translate(-canvas.width / 2, -canvas.height / 2);
    }

    // Apply filters
    const filterString = `
      brightness(${1 + filters.brightness})
      contrast(${1 + filters.contrast})
      saturate(${1 + filters.saturation})
      sepia(${filters.sepia})
      grayscale(${filters.grayscale})
      invert(${filters.invert})
      blur(${filters.blur}px)
      hue-rotate(${filters.hueRotate}deg)
    `;
    ctx.filter = filterString;

    // Draw the cropped portion
    ctx.drawImage(
      image,
      Math.max(0, sourceX),
      Math.max(0, sourceY),
      Math.min(sourceWidth, image.width - sourceX),
      Math.min(sourceHeight, image.height - sourceY),
      0,
      0,
      canvas.width,
      canvas.height
    );

    ctx.restore();

    // Convert to blob and file
    canvas.toBlob((blob) => {
      if (blob) {
        const croppedFile = new File([blob], imageFile.name, { type: imageFile.type });
        const dataUrl = canvas.toDataURL(imageFile.type, 0.9);
        onCropComplete(croppedFile, dataUrl);
      }
      setIsProcessing(false);
    }, imageFile.type, 0.9);
  }, [image, cropArea, position, scale, rotation, filters, imageFile, onCropComplete]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96 bg-gray-100 dark:bg-gray-800 rounded-lg">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-900 rounded-lg p-6 max-w-4xl mx-auto">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
          Crop & Edit Image
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Drag to reposition, drag crop area to move it, use controls to zoom and rotate
        </p>
      </div>

      {/* Main Crop Area */}
      <div className="flex justify-center mb-6">
        <div 
          ref={containerRef}
          className="relative w-full max-w-96 h-64 sm:h-80 md:h-96 bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden border-2 border-gray-300 dark:border-gray-600 mx-4 sm:mx-0"
          onMouseMove={(e) => {
            handleMouseMove(e);
            handleCropMouseMove(e);
          }}
          onMouseUp={() => {
            handleMouseUp();
            handleCropMouseUp();
          }}
          onMouseLeave={() => {
            handleMouseUp();
            handleCropMouseUp();
          }}
          onTouchMove={(e) => {
            e.preventDefault();
            const touch = e.touches[0];
            const rect = containerRef.current?.getBoundingClientRect();
            if (rect && touch) {
              const mouseEvent = {
                clientX: touch.clientX,
                clientY: touch.clientY,
                preventDefault: () => {},
                stopPropagation: () => {}
              } as any;
              handleMouseMove(mouseEvent);
              handleCropMouseMove(mouseEvent);
            }
          }}
          onTouchEnd={() => {
            handleMouseUp();
            handleCropMouseUp();
          }}
        >
          {image && (
            <>
              {/* Filtered Image Preview */}
              <canvas
                ref={previewCanvasRef}
                className="absolute cursor-move select-none touch-manipulation"
                style={{
                  transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
                  transformOrigin: 'center',
                  left: '50%',
                  top: '50%',
                  marginLeft: rotation === 90 || rotation === 270 ? `-${image.height / 2}px` : `-${image.width / 2}px`,
                  marginTop: rotation === 90 || rotation === 270 ? `-${image.width / 2}px` : `-${image.height / 2}px`,
                  maxWidth: 'none',
                  maxHeight: 'none'
                }}
                onMouseDown={handleMouseDown}
                onTouchStart={(e) => {
                  e.preventDefault();
                  const touch = e.touches[0];
                  const rect = containerRef.current?.getBoundingClientRect();
                  if (rect && touch) {
                    const mouseEvent = {
                      clientX: touch.clientX,
                      clientY: touch.clientY,
                      preventDefault: () => {},
                      stopPropagation: () => {}
                    } as any;
                    handleMouseDown(mouseEvent);
                  }
                }}
                draggable={false}
              />
              
              {/* Crop Overlay */}
              <div className="absolute inset-0 bg-black bg-opacity-50">
                <div
                  className="absolute border-2 border-white shadow-lg cursor-move touch-manipulation"
                  style={{
                    left: cropArea.x,
                    top: cropArea.y,
                    width: cropArea.width,
                    height: cropArea.height,
                    backgroundColor: 'transparent'
                  }}
                  onMouseDown={handleCropMouseDown}
                  onTouchStart={(e) => {
                    e.preventDefault();
                    const touch = e.touches[0];
                    const rect = containerRef.current?.getBoundingClientRect();
                    if (rect && touch) {
                      const mouseEvent = {
                        clientX: touch.clientX,
                        clientY: touch.clientY,
                        preventDefault: () => {},
                        stopPropagation: () => {}
                      } as any;
                      handleCropMouseDown(mouseEvent);
                    }
                  }}
                >
                  {/* Corner handles */}
                  <div className="absolute -top-1 -left-1 w-3 h-3 bg-white border border-gray-400 rounded-full"></div>
                  <div className="absolute -top-1 -right-1 w-3 h-3 bg-white border border-gray-400 rounded-full"></div>
                  <div className="absolute -bottom-1 -left-1 w-3 h-3 bg-white border border-gray-400 rounded-full"></div>
                  <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-white border border-gray-400 rounded-full"></div>
                  
                  {/* Grid lines */}
                  <div className="absolute inset-0 grid grid-cols-3 grid-rows-3 opacity-50">
                    {Array.from({ length: 9 }).map((_, i) => (
                      <div key={i} className="border border-white border-opacity-30"></div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Tool Buttons */}
      <div className="flex items-center justify-center space-x-4 mb-6">
        <button
          onClick={handleRotate}
          className="flex items-center space-x-2 px-3 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-lg transition-colors"
        >
          <RotateCw size={16} />
          <span className="text-sm">Rotate</span>
        </button>
        
        {enableSmartCrop && (
          <button
            onClick={handleSmartCrop}
            disabled={isProcessing}
            className="flex items-center space-x-2 px-3 py-2 bg-primary-200 dark:bg-primary-700 hover:bg-primary-300 dark:hover:bg-primary-600 rounded-lg transition-colors disabled:opacity-50"
          >
            <Wand2 size={16} />
            <span className="text-sm">Smart Crop</span>
          </button>
        )}
        
        <button
          onClick={handleReset}
          className="flex items-center space-x-2 px-3 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-lg transition-colors"
        >
          <Move size={16} />
          <span className="text-sm">Reset</span>
        </button>
        
        <button
          onClick={handleToggleCropType}
          className="flex items-center space-x-2 px-3 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-lg transition-colors"
        >
          {currentCropType === 'portrait' ? (
            <Monitor size={16} />
          ) : (
            <Smartphone size={16} />
          )}
          <span className="text-sm">{currentCropType === 'portrait' ? 'Switch to Landscape' : 'Switch to Portrait'}</span>
        </button>
      </div>

      {/* Horizontal Filter Carousel */}
      {enableFilters && (
        <div className="mb-6">
          <div className="flex items-center space-x-2 mb-3">
            <button
              onClick={scrollFiltersLeft}
              className="p-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-lg transition-colors"
            >
              <ChevronLeft size={16} />
            </button>
            
            <div 
              ref={filtersScrollRef}
              className="flex-1 overflow-x-auto scrollbar-hide"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              <div className="flex space-x-3 pb-2">
                {Object.keys(FILTER_PRESETS).map((preset) => (
                  <button
                    key={preset}
                    onClick={() => applyFilterPreset(preset)}
                    className={`flex-shrink-0 px-4 py-2 text-sm rounded-lg transition-colors whitespace-nowrap ${
                      selectedPreset === preset
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {preset.charAt(0).toUpperCase() + preset.slice(1)}
                  </button>
                ))}
              </div>
            </div>
            
            <button
              onClick={scrollFiltersRight}
              className="p-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-lg transition-colors"
            >
              <ChevronRight size={16} />
            </button>
          </div>
          
          {/* Filter Adjustments Toggle */}
          <div className="flex justify-center">
            <button
              onClick={() => setShowFilterAdjustments(!showFilterAdjustments)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                showFilterAdjustments 
                  ? 'bg-primary-200 dark:bg-primary-700' 
                  : 'bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600'
              }`}
            >
              <Sliders size={16} />
              <span className="text-sm">Fine Tune</span>
            </button>
          </div>
        </div>
      )}

      {/* Filter Adjustments Panel */}
      {enableFilters && showFilterAdjustments && (
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 mb-6">
          <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-4 text-center">Fine Tune Adjustments</h4>
          
          <div className="grid grid-cols-2 gap-4">
            {Object.entries(filters).map(([key, value]) => (
              <div key={key}>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  {key.charAt(0).toUpperCase() + key.slice(1)}: {Math.round(value * 100)}
                </label>
                <input
                  type="range"
                  min={key === 'hueRotate' ? -180 : -1}
                  max={key === 'hueRotate' ? 180 : 1}
                  step={key === 'hueRotate' ? 1 : 0.01}
                  value={value}
                  onChange={(e) => {
                    const newValue = parseFloat(e.target.value);
                    setFilters(prev => ({ ...prev, [key]: newValue }));
                    setSelectedPreset('custom');
                  }}
                  className="w-full"
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Zoom Controls */}
      <div className="flex items-center justify-center space-x-4 mb-6">
        <button
          onClick={handleZoomOut}
          className="p-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-lg transition-colors"
          aria-label="Zoom out"
        >
          <ZoomOut size={20} />
        </button>
        
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-600 dark:text-gray-400">Zoom:</span>
          <input
            type="range"
            min="0.1"
            max="3"
            step="0.1"
            value={scale}
            onChange={(e) => setScale(parseFloat(e.target.value))}
            className="w-32"
          />
          <span className="text-sm text-gray-600 dark:text-gray-400 w-12">
            {Math.round(scale * 100)}%
          </span>
        </div>
        
        <button
          onClick={handleZoomIn}
          className="p-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-lg transition-colors"
          aria-label="Zoom in"
        >
          <ZoomIn size={20} />
        </button>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between pt-6">
        <button
          onClick={onCancel}
          className="px-6 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-gray-900 flex items-center space-x-2"
        >
          <X size={16} />
          <span>Cancel</span>
        </button>
        
        <button
          onClick={handleCropComplete}
          disabled={isProcessing}
          className="px-6 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 dark:disabled:bg-gray-600 disabled:cursor-not-allowed border border-transparent rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-gray-900 flex items-center space-x-2"
        >
          {isProcessing ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              <span>Processing...</span>
            </>
          ) : (
            <>
              <Check size={16} />
              <span>Apply Changes</span>
            </>
          )}
        </button>
      </div>

      {/* Hidden canvas for processing */}
      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
};

export default ImageCropTool;
